import { useEffect, useRef } from 'react';

// Удержание фокуса внутри открытого оверлея + Escape для его закрытия.
//
// Две обязанности в одном хуке не от лени: обе упираются в один и тот же
// вопрос — «какой из открытых оверлеев сейчас верхний». Модалка над дровером
// должна и ловить Tab, и закрываться по Escape одна, не утаскивая за собой
// дровер под собой. Разнеси это по двум хукам — и стек пришлось бы держать в
// двух местах синхронно.
//
// Стек модульного уровня, а не контекст: оверлеи рендерятся через порталы в
// document.body и деревом друг друга не видят (PickerSheet из FilterModal —
// как раз такой случай). Провайдер пришлось бы вешать выше порталов и
// протаскивать через каждый из них.
const stack = [];

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Список пересчитывается на КАЖДЫЙ Tab, а не запоминается при открытии:
// содержимое оверлеев меняется на ходу (шаги воронки добавляются, кнопка
// Create разблокируется), и закэшированный список увёл бы фокус в никуда.
const focusablesIn = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // Невидимое не фокусируется: у скрытых вкладок и схлопнутых блоков
    // нулевой бокс. offsetParent не годится — он null и у position: fixed.
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });

// Прячет от скринридера всё, что лежит ВНЕ оверлея. Фокус-трап держит только
// Tab; экранный диктор ходит по документу своими командами и без этого спокойно
// зачитывает навбар и таблицу под подложкой, как будто модалки нет.
//
// Алгоритм — подъём от контейнера к <body> с пометкой соседей на каждом
// уровне, а не «скрыть #root»: ui/Modal намеренно живёт без портала (на
// контексте наложения держится лестница z-индексов), то есть лежит ВНУТРИ
// #root, и скрытие корня спрятало бы саму модалку. Подъём по соседям
// одинаково работает и для неё, и для портальных Drawer/PickerSheet.
//
// aria-hidden, а не inert: доступ с клавиатуры уже закрыт трапом выше, а inert
// вдобавок гасит указатель и в старых WebKit поддержан неполно — два механизма
// на одну задачу разошлись бы при первой же правке.
const hideOutside = (container) => {
  const hidden = [];
  let node = container;

  while (node && node.parentElement) {
    const parent = node.parentElement;
    // Копия на итерацию: node переприсваивается ниже, и замыкание на него
    // внутри forEach читалось бы как ошибка (и стало бы ею, стань обход
    // асинхронным).
    const child = node;

    Array.from(parent.children).forEach((sibling) => {
      if (sibling === child) return;
      // Уже скрыт оверлеем под нами — не наш, и снимать пометку не нам.
      if (sibling.getAttribute('aria-hidden') === 'true') return;
      if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') return;
      sibling.setAttribute('aria-hidden', 'true');
      hidden.push(sibling);
    });

    if (parent === document.body) break;
    node = parent;
  }

  return hidden;
};

/**
 * @param {React.RefObject<HTMLElement>} containerRef корень оверлея
 * @param {{isOpen?: boolean, onEscape?: () => void}} options
 */
const useFocusTrap = (containerRef, { isOpen = true, onEscape } = {}) => {
  // Через ref, чтобы смена onClose у родителя не перевешивала слушатель и не
  // сбрасывала фокус на первый элемент посреди работы с формой.
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  // Куда вернуть фокус после закрытия. Снимок берётся НА ФАЗЕ РЕНДЕРА, а не в
  // эффекте, и это не педантизм: autoFocus внутри оверлея срабатывает в фазе
  // коммита, то есть РАНЬШЕ эффектов. Из эффекта здесь виден уже не триггер, а
  // поле внутри модалки — оно исчезает вместе с ней, и фокус сваливается на
  // <body>. Ровно это и наблюдалось до правки.
  const restoreRef = useRef(null);
  if (isOpen && restoreRef.current === null) {
    restoreRef.current = document.activeElement;
  }

  useEffect(() => {
    if (!isOpen) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const entry = {};
    stack.push(entry);

    const hidden = hideOutside(container);

    // Если внутри уже есть autoFocus-поле (модалка создания токена), не
    // трогаем его. Иначе фокусируем сам контейнер, а не первый элемент:
    // скринридер тогда читает заголовок диалога, а не «Закрыть».
    if (!container.contains(document.activeElement)) {
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus({ preventScroll: true });
    }

    const onKeyDown = (event) => {
      // Не верхний оверлей — не наше событие.
      if (stack[stack.length - 1] !== entry) return;

      if (event.key === 'Escape') {
        if (escapeRef.current) {
          event.stopPropagation();
          escapeRef.current();
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const items = focusablesIn(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!container.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // ВСПЛЫТИЕ, не перехват — и это принципиально. ui/Select,
    // MultiSelectCombobox и ui/ActionMenu гасят Escape через
    // stopPropagation(), чтобы закрыть свою выпадашку, не закрывая модалку
    // вокруг (живой случай — FilterModal с селектами внутри). В фазе
    // перехвата мы бы сработали ДО них и закрывали модалку вместе с выбором
    // варианта.
    //
    // ⚠️ Оговорка на будущее: ActionMenu слушает Escape тоже на document, а
    // stopPropagation() не останавливает соседей по тому же узлу. Сейчас это
    // безопасно — ActionMenu нигде не лежит внутри модалки (потребители:
    // Apps, Funnels, RetentionListWidget). Если положат, там понадобится
    // stopImmediatePropagation().
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const index = stack.indexOf(entry);
      if (index !== -1) stack.splice(index, 1);

      // Снимаем ровно то, что пометили сами: соседа, скрытого оверлеем под
      // нами, трогать нельзя — он ещё открыт.
      hidden.forEach((el) => el.removeAttribute('aria-hidden'));

      // Ref гасим здесь, а не в рендере с isOpen === false: тот рендер
      // проходит РАНЬШЕ этой уборки и стёр бы снимок до того, как мы его
      // прочитаем.
      const restoreTo = restoreRef.current;
      restoreRef.current = null;

      // Возвращаем фокус туда, откуда пришли, — но только если этот элемент
      // ещё в документе: строку таблицы, из которой открыли дровер, могли
      // перерисовать за время работы с ним.
      if (restoreTo instanceof HTMLElement && document.contains(restoreTo)) {
        restoreTo.focus({ preventScroll: true });
      }
    };
  }, [isOpen, containerRef]);
};

export default useFocusTrap;
